import React, { useState, useEffect, useRef } from 'react';
import {
  Truck,
  UserCheck,
  Camera,
  Signature,
  RotateCcw,
  Loader2,
  XCircle,
  CheckCircle2,
  History,
  Navigation,
  ExternalLink,
  Search,
  Filter,
  PackageCheck,
  Printer,
  ChevronRight,
  ArrowLeft,
  Barcode,
  AlertTriangle,
  ClipboardCheck,
  Settings,
  MoreHorizontal,
  FileDown,
  Upload,
  FileSpreadsheet,
  Repeat,
  Bell
} from 'lucide-react';
import { cn } from '../lib/utils';
import { collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  increment,
  getDoc,
  getDocs,
  addDoc,
  arrayUnion } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from '../firebase';
import {
  db,
  storage,
  auth,
  handleFirestoreError,
  OperationType
} from '../firebase';
import { DocumentPrintLayout } from './ui/DocumentPrintLayout';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DocumentChatterPanel from './DocumentChatterPanel';
import { CancelDocumentDialog, StatusBadge } from './workflow';
import { normalizeDocumentStatus, type DocumentStatus } from '../lib/documentStatus';
import { notify } from '../services/notify';
import { logHistory } from '../services/historyService';
import { postInventoryJournal } from '../services/accountingService';
import { reduceStockFIFO } from '../services/inventoryService';

// Re-using interfaces for consistency
type LogisticsStage = 'draft' | 'assigned' | 'shipped' | 'delivered' | 'returned';

interface DeliveryNoteItem {
  itemId: string;
  itemCode: string;
  name: string;
  description: string;
  quantityOrdered: number;
  quantityShipped: number;
  unitPrice: number;
  lotNumber?: string;
  expiryDate?: string;
  quoteLineId?: string;
}

interface DeliveryNote {
  id: string;
  number: string;
  invoiceId: string;
  invoiceNumber: string;
  sourceInvoiceId?: string;
  sourceInvoiceNumber?: string;
  sourceOrderId?: string;
  sourceOrderNumber?: string;
  sourceQuoteId?: string;
  sourceQuoteNumber?: string;
  orgId: string;
  contactId: string;
  contactName: string;
  items: DeliveryNoteItem[];
  assignedPersonnelId: string;
  assignedPersonnelName: string;
  assignedPersonnelEmail?: string;
  status: DocumentStatus;
  logisticsStage: LogisticsStage;
  receiverName?: string;
  signatureUrl?: string;
  photoUrl?: string;
  notes?: string;
  createdAt: any;
  shippedAt?: any;
  deliveredAt?: any;
  updatedAt: any;
  brandingTheme?: string;
  storeId?: string;
  warehouseId?: string;
  warehouseName?: string;
}

interface DeliveriesViewProps {
  orgId: string;
  language: 'en' | 'ar';
  initialDeliveryId?: string | null;
  onClearInitial?: () => void;
  onNavigate?: (tab: string, id?: string) => void;
}

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');

const isLocalSession = () => Boolean(getLocalAuthToken());

const getLocalUserId = () => {
  try {
    const payload = getLocalAuthToken()?.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))).sub || null;
  } catch {
    return null;
  }
};

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

const deriveLogisticsStage = (status: unknown, explicitStage?: unknown): LogisticsStage => {
  const value = String(explicitStage ?? status ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (value === 'assigned') return 'assigned';
  if (value === 'shipped' || value === 'checkingout') return 'shipped';
  if (value === 'delivered') return 'delivered';
  if (value === 'returned') return 'returned';
  return 'draft';
};

const normalizeDelivery = (document: any): DeliveryNote => {
  const metadata = document.metadata || {};
  const rawStatus = document.status ?? metadata.status ?? 'draft';
  const invoiceId = document.sourceInvoiceId ?? metadata.sourceInvoiceId ?? document.invoiceId ?? metadata.invoiceId ?? metadata.sourceDocumentId ?? '';
  const invoiceNumber = document.sourceInvoiceNumber ?? metadata.sourceInvoiceNumber ?? document.invoiceNumber ?? metadata.invoiceNumber ?? '';
  return {
    ...metadata,
    ...document,
    id: document.id,
    number: document.number ?? document.deliveryNumber ?? metadata.number ?? metadata.deliveryNumber ?? `DLV-${String(document.id || '').slice(-6).toUpperCase()}`,
    invoiceId,
    invoiceNumber,
    sourceInvoiceId: document.sourceInvoiceId ?? metadata.sourceInvoiceId ?? invoiceId,
    sourceInvoiceNumber: document.sourceInvoiceNumber ?? metadata.sourceInvoiceNumber ?? invoiceNumber,
    sourceOrderId: document.sourceOrderId ?? metadata.sourceOrderId ?? document.orderId ?? metadata.orderId,
    sourceOrderNumber: document.sourceOrderNumber ?? metadata.sourceOrderNumber ?? document.orderNumber ?? metadata.orderNumber,
    sourceQuoteId: document.sourceQuoteId ?? metadata.sourceQuoteId ?? document.quoteId ?? metadata.quoteId,
    sourceQuoteNumber: document.sourceQuoteNumber ?? metadata.sourceQuoteNumber ?? document.quoteNumber ?? metadata.quoteNumber,
    orgId: document.orgId ?? document.org_id ?? '',
    contactId: document.contactId ?? document.contact_id ?? '',
    contactName: document.contactName ?? metadata.contactName ?? '',
    items: Array.isArray(document.items) ? document.items.map((item: any) => ({ ...item, quoteLineId: item.quoteLineId ?? item.sourceQuoteLineId })) : [],
    assignedPersonnelId: document.assignedPersonnelId ?? metadata.assignedPersonnelId ?? '',
    assignedPersonnelName: document.assignedPersonnelName ?? metadata.assignedPersonnelName ?? '',
    status: normalizeDocumentStatus(rawStatus),
    logisticsStage: deriveLogisticsStage(rawStatus, document.logisticsStage ?? metadata.logisticsStage),
    createdAt: document.createdAt ?? document.created_at,
    updatedAt: document.updatedAt ?? document.updated_at,
  } as DeliveryNote;
};

const logisticsLabel = (stage: LogisticsStage, language: 'en' | 'ar') => ({
  en: { draft: 'Draft', assigned: 'Assigned', shipped: 'Shipped', delivered: 'Delivered', returned: 'Returned' },
  ar: { draft: 'مسودة', assigned: 'تم التعيين', shipped: 'تم الشحن', delivered: 'تم التسليم', returned: 'مرتجع' },
}[language][stage]);

export default function DeliveriesView({ orgId, language = 'en', initialDeliveryId, onClearInitial, onNavigate }: DeliveriesViewProps) {
  const [deliveries, setDeliveries] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandingThemes, setBrandingThemes] = useState<any[]>([]);
  const [activeDelivery, setActiveDelivery] = useState<DeliveryNote | null>(null);
  const [isPODModalOpen, setIsPODModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnQuantities, setReturnQuantities] = useState<{[key: string]: number}>({});
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isFinalizingCheckOut, setIsFinalizingCheckOut] = useState(false);
  const [stockStatus, setStockStatus] = useState<{[key: string]: { onHand: number, trackInventory: boolean, available: boolean }}>({});
  const [loadingStock, setLoadingStock] = useState(false);
  const [isSubmittingPOD, setIsSubmittingPOD] = useState(false);
  const [currentUserOrgId, setCurrentUserOrgId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [podData, setPodData] = useState({
    receiverName: '',
    signature: '',
    photo: null as File | null,
    notes: ''
  });

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    id: true,
    invoiceNumber: true,
    contact: true,
    status: true,
    orderedQty: true,
    deliveredQty: true,
    assignedTo: true,
    createdAt: true,
    deliveredAt: true,
    value: true,
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'id', 'invoiceNumber', 'contact', 'status', 'orderedQty', 'deliveredQty', 'assignedTo', 'createdAt', 'deliveredAt', 'value'
  ]);
  const [showColumnGear, setShowColumnGear] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'draft' | 'assigned' | 'shipped' | 'delivered' | 'returned'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [refFilter, setRefFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [columnGearOpen, setColumnGearOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      void localApiRequest<any[]>(`/api/organizations`)
        .then((organizations) => {
          const organization = Array.isArray(organizations) ? organizations.find(entry => entry.id === orgId) || organizations[0] : null;
          if (!cancelled && organization) setOrganization(organization);
        })
        .catch(error => {
          if (!cancelled) console.error('Local organization load failed:', error);
        });
      return () => { cancelled = true; };
    }

    const fetchOrg = async () => {
      const docSnap = await getDoc(doc(db, 'organizations', orgId));
      if (docSnap.exists()) {
        setOrganization({ id: docSnap.id, ...docSnap.data() });
      }
    };
    void fetchOrg();
  }, [orgId]);

  const sigCanvas = useRef<any>(null);

  // Fetch current user's org_user ID to match with assignedPersonnelId
  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      const localUserId = getLocalUserId();
      void localApiRequest<any[]>(`/api/organizations/${orgId}/users`)
        .then((users) => {
          const membership = Array.isArray(users) ? users.find(user => user.userId === localUserId || user.user_id === localUserId) : null;
          if (!cancelled) setCurrentUserOrgId(membership?.id || localUserId);
        })
        .catch(error => {
          if (!cancelled) console.error('Local delivery user lookup failed:', error);
        });
      return () => { cancelled = true; };
    }

    if (!auth.currentUser?.email) return;
    const q = query(
      collection(db, 'org_users'),
      where('email', '==', auth.currentUser.email.toLowerCase())
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        // Find the specific org_user for this org
        const doc = snapshot.docs.find(d => {
          const data = d.data();
          return data.orgId === orgId || data.memberships?.some((m: any) => m.orgId === orgId);
        }) || snapshot.docs[0];
        setCurrentUserOrgId(doc.id);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'org_users');
    });
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    const localAuthToken = localStorage.getItem('app_auth_token');
    if (localAuthToken) {
      let cancelled = false;

      const fetchLocalDeliveries = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/documents?types=delivery`, {
            headers: { Authorization: `Bearer ${localAuthToken}` }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch deliveries');
          }

          const payload = await response.json();
          const documents = Array.isArray(payload) ? payload : payload.documents || [];
          if (!cancelled) setDeliveries(documents.map(normalizeDelivery));
        } catch (error) {
          console.error('Error fetching local deliveries:', error);
          if (!cancelled) {
            notify.error(language === 'ar' ? 'تعذر تحميل عمليات التوصيل' : 'Failed to load deliveries');
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      fetchLocalDeliveries();
      return () => {
        cancelled = true;
      };
    }

    if (!auth.currentUser) return;

    // Fetch deliveries assigned to the current user OR all if admin
    const q = query(
      collection(db, 'delivery_notes'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(snapshotDoc => normalizeDelivery({ id: snapshotDoc.id, ...snapshotDoc.data() }));
      // Optionally filter by assigned personnel if not admin
      // For this demo, we'll show all but highlight assigned
      setDeliveries(docs);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'delivery_notes');
    });

    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (initialDeliveryId && deliveries.length > 0) {
      const delivery = deliveries.find(d => d.id === initialDeliveryId);
      if (delivery) {
        setActiveDelivery(delivery);
        setHighlightedId(delivery.id);

        // Scroll to it
        setTimeout(() => {
          const element = document.getElementById(`delivery-${delivery.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        // Clear highlight after 5 seconds
        setTimeout(() => setHighlightedId(null), 5000);

        if (onClearInitial) onClearInitial();
      }
    }
  }, [initialDeliveryId, deliveries, currentUserOrgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      void localApiRequest<any[]>(`/api/organizations/${orgId}/branding-themes`)
        .then((themes) => {
          if (!cancelled) setBrandingThemes(Array.isArray(themes) ? themes : []);
        })
        .catch(error => {
          if (!cancelled) console.error('Local delivery branding themes load failed:', error);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'branding_themes'), where('orgId', '==', orgId));
    return onSnapshot(q, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, [orgId]);

  const handlePrintDeliveryNote = (dn: DeliveryNote) => {
    setDocToPrint(dn);
    setIsPrintModalOpen(true);
  };

  const handleCheckOut = async (dn: DeliveryNote) => {
    setActiveDelivery(dn);
    setIsCheckOutModalOpen(true);
    setLoadingStock(true);

    try {
      if (isLocalSession()) {
        const statusUpdates: {[key: string]: any} = {};
        const quants = await localApiRequest<any[]>(`/api/organizations/${orgId}/inventory-quants`);
        for (const item of dn.items || []) {
          const itemQuant = (Array.isArray(quants) ? quants : []).filter((candidate: any) => candidate.productId === item.itemId && candidate.locationId === (dn.warehouseId || dn.storeId));
          const onHand = itemQuant.reduce((sum: number, candidate: any) => sum + Number(candidate.quantityOnHand || 0) - Number(candidate.quantityReserved || 0), 0);
          const trackInventory = true;
          statusUpdates[item.itemId] = {
            onHand,
            trackInventory,
            available: !trackInventory || onHand >= Number(item.quantityShipped || 0),
          };
        }
        setStockStatus(statusUpdates);
        return;
      }

      const statusUpdates: {[key: string]: any} = {};

      for (const item of (dn.items || [])) {
        let itemData: any = null;

        // 1. Try by ID
        const itemDoc = await getDoc(doc(db, 'items', item.itemId));
        if (itemDoc.exists()) {
          itemData = itemDoc.data();
        } else {
          // 2. Fallback to lookup by code
          const q = query(
            collection(db, 'items'),
            where('orgId', '==', orgId),
            where('code', '==', item.itemCode)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            itemData = snap.docs[0].data();
          }
        }

        if (itemData) {
          const trackInventory = itemData.trackInventory ?? false;
          const onHand = itemData.quantity ?? 0;

          statusUpdates[item.itemId] = {
            onHand,
            trackInventory,
            available: !trackInventory || onHand >= item.quantityShipped
          };
        } else {
          statusUpdates[item.itemId] = {
            onHand: 0,
            trackInventory: false,
            available: true
          };
        }
      }

      setStockStatus(statusUpdates);
    } catch (err) {
      console.error("Error fetching stock levels:", err);
      notify.error("Failed to verify stock levels");
    } finally {
      setLoadingStock(false);
    }
  };

  const finalizeCheckOut = async () => {
    if (!activeDelivery) return;

    // Check if any tracked items have shortage
    const shortages = (activeDelivery.items || []).filter(item => {
      const status = stockStatus[item.itemId];
      return status?.trackInventory && !status.available;
    });

    if (shortages.length > 0) {
      notify.error(`Cannot check out: Shortage for ${shortages.length} item(s)`);
      return;
    }

    setIsFinalizingCheckOut(true);
    try {
      if (isLocalSession()) {
        const result = await localApiRequest<any>(`/api/organizations/${orgId}/delivery-notes/${activeDelivery.id}/checkout`, { method: 'POST' });
        const updated = normalizeDelivery(result.delivery);
        setDeliveries(current => current.map(delivery => delivery.id === updated.id ? updated : delivery));
        setActiveDelivery(updated);
        notify.confirm('Delivery checked out and marked as Shipped!');
        setIsCheckOutModalOpen(false);
        return;
      }

      await updateDoc(doc(db, 'delivery_notes', activeDelivery.id), {
        status: 'done',
        logisticsStage: 'shipped',
        shippedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion({
          action: 'Checked Out',
          user: auth.currentUser?.displayName || 'Unknown',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: 'Delivery checked out and inventory verified.'
        })
      });

      // Per SYSTEM_DOCUMENTATION §3.3.2, Check Out/Dispatch only validates stock and
      // marks the delivery as Shipped. NO GL journal or stock deduction happens here;
      // COGS/Inventory is posted at Confirm Delivery (POD) in handleConfirmPOD.

      notify.confirm("Delivery checked out and marked as Shipped!");
      await logHistory(orgId, 'Delivery Checked Out', 'Delivery checked out and inventory verified.', auth.currentUser, activeDelivery.id, 'Delivery');
      setIsCheckOutModalOpen(false);
      setActiveDelivery(null);
    } catch (err) {
      console.error(err);
      notify.error("Failed to finalize check out");
    } finally {
      setIsFinalizingCheckOut(false);
    }
  };

  const handleConfirmPOD = async () => {
    if (!activeDelivery) return;

    const localAuthToken = localStorage.getItem('app_auth_token');
    if (localAuthToken) {
      if (!podData.receiverName) {
        notify.error("Receiver name is mandatory");
        return;
      }
      if (sigCanvas.current?.isEmpty()) {
        notify.error("Signature is mandatory");
        return;
      }

      const locationId = activeDelivery.warehouseId || activeDelivery.storeId || localStorage.getItem('selected_pos_store_id');
      if (!locationId) {
        notify.error('A warehouse or store location is required to complete this delivery');
        return;
      }

      setIsSubmittingPOD(true);
      try {
        const signatureUrl = sigCanvas.current!.getCanvas().toDataURL('image/png');
        let photoUrl: string | null = null;
        if (podData.photo) {
          const formData = new FormData();
          formData.append('file', podData.photo);
          formData.append('targetPath', `delivery_pod/${activeDelivery.id}/${Date.now()}_${podData.photo.name}`);
          const uploadResponse = await fetch('/api/files/upload', {
            method: 'POST',
            headers: { Authorization: `Bearer ${localAuthToken}` },
            body: formData,
          });
          const upload = await uploadResponse.json().catch(() => null);
          if (!uploadResponse.ok) throw new Error(upload?.error || 'Failed to upload proof-of-delivery photo');
          const file = upload?.file || upload;
          photoUrl = file?.url || file?.downloadUrl || file?.downloadURL || null;
        }

        const result = await localApiRequest<any>(`/api/organizations/${orgId}/delivery-notes/${activeDelivery.id}/pod`, {
          method: 'POST',
          body: JSON.stringify({ receiverName: podData.receiverName, signatureUrl, ...(photoUrl ? { photoUrl } : {}), notes: podData.notes }),
        });
        const updated = normalizeDelivery(result.delivery);
        setDeliveries(current => current.map(delivery => delivery.id === updated.id ? updated : delivery));
        notify.confirm('Successfully delivered and inventory updated!');
        setIsPODModalOpen(false);
        setActiveDelivery(null);
        setPodData({ receiverName: '', signature: '', photo: null, notes: '' });
        return;
      } catch (error) {
        console.error(error);
        notify.error('Failed to confirm delivery');
        return;
      } finally {
        setIsSubmittingPOD(false);
      }
    }

    if (!auth.currentUser) return;
    if (!podData.receiverName) {
      notify.error("Receiver name is mandatory");
      return;
    }
    if (sigCanvas.current?.isEmpty()) {
      notify.error("Signature is mandatory");
      return;
    }

    setIsSubmittingPOD(true);
    try {
      let signatureUrl = '';
      let photoUrl = '';

      // 1. Signature (Base64)
      signatureUrl = sigCanvas.current!.getCanvas().toDataURL('image/png');

      // 2. Photo (Base64 + Compressed)
      if (podData.photo) {
        photoUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const MAX_WIDTH = 800;
              const MAX_HEIGHT = 800;
              let width = img.width;
              let height = img.height;

              if (width > height) {
                if (width > MAX_WIDTH) {
                  height *= MAX_WIDTH / width;
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width *= MAX_HEIGHT / height;
                  height = MAX_HEIGHT;
                }
              }

              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx?.drawImage(img, 0, 0, width, height);
              resolve(canvas.toDataURL('image/jpeg', 0.6));
            };
            img.src = e.target?.result as string;
          };
          reader.readAsDataURL(podData.photo!);
        });
      }

      // 3. Update Delivery Note
      const updateData: any = {
        status: 'done',
        logisticsStage: 'delivered',
        deliveredAt: serverTimestamp(),
        receiverName: podData.receiverName,
        signatureUrl,
        notes: podData.notes,
        updatedAt: serverTimestamp()
      };
      if (photoUrl) updateData.photoUrl = photoUrl;

      await updateDoc(doc(db, 'delivery_notes', activeDelivery.id), updateData);

      // 4. Deduct Inventory
      for (const item of (activeDelivery.items || [])) {
        const itemRef = doc(db, 'items', item.itemId);
        const itemSnap = await getDoc(itemRef);

        if (itemSnap.exists()) {
          const itemData = itemSnap.data();
          if (itemData.trackInventory) {
            const warehouseId = activeDelivery.warehouseId || activeDelivery.storeId || 'default';

            // Use unified FIFO service
            await reduceStockFIFO(item.itemId, item.quantityShipped, warehouseId);

            const productLogEntry = {
              action: 'Inventory Decreased (Sales Delivery - FIFO)',
              by: auth.currentUser?.displayName || 'Unknown User',
              byEmail: auth.currentUser?.email || 'No Email',
              at: new Date().toISOString(),
              details: `Quantity decreased by ${item.quantityShipped} via Delivery Note #${activeDelivery.id.slice(-6).toUpperCase()} using FIFO`,
              relatedId: activeDelivery.id,
              relatedType: 'delivery_note'
            };

            await updateDoc(itemRef, {
              actionLog: arrayUnion(productLogEntry),
              updatedAt: serverTimestamp()
            });

            // Add stock movement record
            await addDoc(collection(db, 'stock_movements'), {
              itemId: item.itemId,
              storeId: warehouseId,
              type: 'sale',
              quantity: -item.quantityShipped,
              referenceId: activeDelivery.id,
              referenceNumber: activeDelivery.invoiceNumber || activeDelivery.id,
              createdBy: auth.currentUser?.uid,
              createdAt: serverTimestamp(),
              orgId: activeDelivery.orgId,
              note: `Delivery Note #${activeDelivery.id.slice(-6).toUpperCase()}`
            });
          }
        }
      }

      // Per SYSTEM_DOCUMENTATION §3.3.3, COGS/Inventory GL journal is posted at
      // Confirm Delivery (POD), matching the physical stock deduction above.
      await postInventoryJournal(orgId, activeDelivery, auth.currentUser.uid);

      notify.confirm('Successfully delivered and inventory updated!');
      await logHistory(orgId, 'Delivery Completed', `Successfully delivered and items received by ${podData.receiverName}`, auth.currentUser, activeDelivery.id, 'Delivery');
      setIsPODModalOpen(false);
      setActiveDelivery(null);
      setPodData({ receiverName: '', signature: '', photo: null, notes: '' });
    } catch (error) {
      console.error(error);
      notify.error('Failed to confirm delivery');
    } finally {
      setIsSubmittingPOD(false);
    }
  };

  const handleConfirmReturn = async () => {
    if (!activeDelivery) return;

    const localAuthToken = localStorage.getItem('app_auth_token');
    if (localAuthToken) {
      const returnItems = activeDelivery.items.filter(item => (returnQuantities[item.itemId] || 0) > 0);
      if (returnItems.length === 0) {
        notify.error('Please select items to return');
        return;
      }

      const locationId = activeDelivery.warehouseId || activeDelivery.storeId || localStorage.getItem('selected_pos_store_id');
      if (!locationId) {
        notify.error('A warehouse or store location is required to process this return');
        return;
      }

      const returnReason = (activeDelivery as DeliveryNote & { returnReason?: string }).returnReason;
      setIsSubmittingReturn(true);
      try {
        const localItems = await localApiRequest<any[]>(`/api/organizations/${orgId}/items`);
        for (const item of returnItems) {
          const itemData = (Array.isArray(localItems) ? localItems : []).find((candidate: any) => candidate.id === item.itemId);
          if (!itemData?.trackInventory) continue;
          await localApiRequest(`/api/organizations/${orgId}/inventory-returns`, {
            method: 'POST',
            body: JSON.stringify({
              productId: item.itemId,
              locationId,
              quantity: returnQuantities[item.itemId],
              unitCost: item.unitPrice,
              deliveryDocumentId: activeDelivery.id,
              ...(returnReason ? { reason: returnReason } : {}),
              notes: activeDelivery.notes,
            }),
          });
        }

        const updated = normalizeDelivery(await localApiRequest<any>(`/api/organizations/${orgId}/documents/${activeDelivery.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            status: 'done',
            logisticsStage: 'returned',
            returnedItems: returnItems.map(item => ({ ...item, quantityReturned: returnQuantities[item.itemId] })),
            returnedAt: new Date().toISOString(),
          }),
        }));
        setDeliveries(current => current.map(delivery => delivery.id === updated.id ? updated : delivery));
        notify.confirm('Return processed successfully');
        setIsReturnModalOpen(false);
        setActiveDelivery(null);
        setReturnQuantities({});
        return;
      } catch (error) {
        console.error(error);
        notify.error('Failed to process return');
        return;
      } finally {
        setIsSubmittingReturn(false);
      }
    }

    setIsSubmittingReturn(true);
    try {
      const returnItems = activeDelivery.items.filter(item => (returnQuantities[item.itemId] || 0) > 0);
      if (returnItems.length === 0) {
        notify.error('Please select items to return');
        return;
      }

      // 1. Create a return record / update delivery note
      await updateDoc(doc(db, 'delivery_notes', activeDelivery.id), {
        status: 'done',
        logisticsStage: 'returned',
        returnedItems: returnItems.map(item => ({
          ...item,
          quantityReturned: returnQuantities[item.itemId]
        })),
        returnedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion({
          action: 'Items Returned',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: `Returned items from delivery #{activeDelivery.id.slice(-6).toUpperCase()}`
        })
      });

      await logHistory(orgId, 'Items Returned', `Returned items from delivery #${activeDelivery.id.slice(-6).toUpperCase()}`, auth.currentUser, activeDelivery.id, 'Delivery');

      // 2. Update Inventory
      for (const item of returnItems) {
        const itemRef = doc(db, 'items', item.itemId);
        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists() && itemSnap.data().trackInventory) {
          const qty = returnQuantities[item.itemId];
          const productLogEntry = {
            action: 'Inventory Increased (Sales Return)',
            by: auth.currentUser?.displayName || 'Unknown User',
            byEmail: auth.currentUser?.email || 'No Email',
            at: new Date().toISOString(),
            details: `Quantity increased by ${qty} via Return for Delivery Note #${activeDelivery.id.slice(-6).toUpperCase()}`,
            relatedId: activeDelivery.id,
            relatedType: 'delivery_note'
          };

          const warehouseId = activeDelivery.warehouseId || activeDelivery.storeId || 'default';
          let updatedStockRecords = [...(itemSnap.data().stockRecords || [])];

          // For returns, we just add back to the 'AUTO' lot or create a new return lot
          updatedStockRecords.push({
            id: Math.random().toString(36).substr(2, 9),
            warehouseId: warehouseId,
            warehouseName: activeDelivery.warehouseName || 'Main Warehouse',
            quantity: qty,
            lotNumber: 'RETURNED',
            createdAt: new Date().toISOString()
          });

          await updateDoc(itemRef, {
            quantity: increment(qty),
            [`storeStock.${warehouseId}`]: increment(qty),
            stockRecords: updatedStockRecords,
            actionLog: arrayUnion(productLogEntry),
            updatedAt: serverTimestamp()
          });

          // Add stock movement record
          await addDoc(collection(db, 'stock_movements'), {
            itemId: item.itemId,
            storeId: warehouseId,
            type: 'return',
            quantity: qty,
            referenceId: activeDelivery.id,
            referenceNumber: activeDelivery.invoiceNumber || activeDelivery.id,
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            orgId: activeDelivery.orgId,
            note: `Return for Delivery Note #${activeDelivery.id.slice(-6).toUpperCase()}`
          });
        }
      }

      notify.confirm('Return processed successfully');
      setIsReturnModalOpen(false);
      setActiveDelivery(null);
      setReturnQuantities({});
    } catch (error) {
      console.error(error);
      notify.error('Failed to process return');
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  const handleCancelDelivery = async (reason: string) => {
    if (!activeDelivery) return;
    setIsCancelling(true);
    try {
      if (isLocalSession()) {
        const updated = normalizeDelivery(await localApiRequest<any>(`/api/organizations/${orgId}/documents/${activeDelivery.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'cancelled', cancellationReason: reason, cancelledAt: new Date().toISOString() }),
        }));
        setDeliveries(current => current.map(delivery => delivery.id === updated.id ? updated : delivery));
        setActiveDelivery(updated);
      } else {
        await updateDoc(doc(db, 'delivery_notes', activeDelivery.id), {
          status: 'cancelled',
          cancellationReason: reason,
          cancelledAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          actionLog: arrayUnion({
            action: 'Delivery Cancelled',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: reason,
          }),
        });
        setDeliveries(current => current.map(delivery => delivery.id === activeDelivery.id ? { ...delivery, status: 'cancelled' } : delivery));
        setActiveDelivery(current => current ? { ...current, status: 'cancelled' } : current);
        await logHistory(orgId, 'Delivery Cancelled', reason, auth.currentUser, activeDelivery.id, 'Delivery');
      }
      notify.confirm(language === 'ar' ? 'تم إلغاء مستند التوصيل' : 'Delivery cancelled');
    } catch (error) {
      console.error(error);
      notify.error(language === 'ar' ? 'تعذر إلغاء مستند التوصيل' : 'Failed to cancel delivery');
      throw error;
    } finally {
      setIsCancelling(false);
    }
  };

  const handleExportCSV = () => {
    if (filteredDeliveries.length === 0) {
      notify.error('No deliveries to export');
      return;
    }

    const headers = ['ID', 'Invoice', 'Customer', 'Status', 'Ordered Qty', 'Delivered Qty', 'Assigned To', 'Created Date', 'Delivered Date', 'Value'];
    const rows = filteredDeliveries.map(dn => {
      const ordered = (dn.items || []).reduce((sum, item) => sum + (item.quantityOrdered || 0), 0);
      const delivered = (dn.items || []).reduce((sum, item) => sum + (item.quantityShipped || 0), 0);
      return [
        dn.number,
        dn.sourceInvoiceNumber || dn.invoiceNumber,
        dn.contactName,
        `${dn.status} / ${dn.logisticsStage}`,
        ordered,
        delivered,
        dn.assignedPersonnelName,
        dn.createdAt?.toDate ? dn.createdAt.toDate().toISOString() : dn.createdAt,
        dn.deliveredAt ? (dn.deliveredAt?.toDate ? dn.deliveredAt.toDate().toISOString() : dn.deliveredAt) : '',
        calculateDeliveryValue(dn)
      ];
    });

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `deliveries_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notify.confirm('CSV exported successfully');
  };

  const handleExportExcel = () => {
    notify.confirm('Excel export feature coming soon');
  };

  const handleImport = () => {
    notify.confirm('Import feature coming soon');
  };

  const calculateDeliveryValue = (dn: DeliveryNote) => {
    return (dn.items || []).reduce((sum, item) => sum + (item.quantityShipped * (item.unitPrice || 0)), 0);
  };

  const filteredDeliveries = deliveries.filter(d => {
    // Stage 1: Date Range
    if (dateRange.start) {
      const start = new Date(dateRange.start);
      const deliveryDate = d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt);
      if (deliveryDate < start) return false;
    }
    if (dateRange.end) {
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
      const deliveryDate = d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt);
      if (deliveryDate > end) return false;
    }

    // Stage 2: Specific Advanced Filters
    if (refFilter && !d.number.toLowerCase().includes(refFilter.toLowerCase()) && !(d.sourceInvoiceNumber || d.invoiceNumber || '').toLowerCase().includes(refFilter.toLowerCase())) return false;
    if (customerFilter && !d.contactName.toLowerCase().includes(customerFilter.toLowerCase())) return false;
    if (assignedToFilter && !(d.assignedPersonnelName || '').toLowerCase().includes(assignedToFilter.toLowerCase())) return false;

    // Amount Filters
    const deliveryValue = calculateDeliveryValue(d);
    if (minAmount && deliveryValue < parseFloat(minAmount)) return false;
    if (maxAmount && deliveryValue > parseFloat(maxAmount)) return false;

    // Stage 3: Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = d.id.toLowerCase().includes(q) ||
                    d.number.toLowerCase().includes(q) ||
                    (d.sourceInvoiceNumber || d.invoiceNumber || '').toLowerCase().includes(q) ||
                    (d.sourceOrderNumber || '').toLowerCase().includes(q) ||
                    (d.sourceQuoteNumber || '').toLowerCase().includes(q) ||
                    d.contactName.toLowerCase().includes(q) ||
                    (d.assignedPersonnelName || '').toLowerCase().includes(q);
      if (!match) return false;
    }

    // Stage 4: Status Tabs
    if (filter !== 'all') {
       if (filter === 'draft' && d.logisticsStage !== 'draft') return false;
       if (filter === 'assigned' && d.logisticsStage !== 'assigned') return false;
       if (filter === 'shipped' && d.logisticsStage !== 'shipped') return false;
       if (filter === 'delivered' && d.logisticsStage !== 'delivered') return false;
       if (filter === 'returned' && d.logisticsStage !== 'returned') return false;
    }

    return true;
  });

  const myPendingCount = deliveries.filter(d =>
    (d.assignedPersonnelId === currentUserOrgId || d.assignedPersonnelId === auth.currentUser?.uid) &&
    d.logisticsStage !== 'delivered'
  ).length;

  const myCompletedCount = deliveries.filter(d =>
    (d.assignedPersonnelId === currentUserOrgId || d.assignedPersonnelId === auth.currentUser?.uid) &&
    d.logisticsStage === 'delivered'
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#141414]/20" />
      </div>
    );
  }

  return (
    <div id="deliveries-view-container" className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div id="deliveries-header-section" className="flex flex-col gap-4 bg-white p-6 rounded-3xl border border-border shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary rounded-2xl shadow-lg ring-4 ring-primary/10">
              <Truck className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold italic serif tracking-tight text-primary">{language === 'ar' ? 'عمليات التوصيل' : 'Deliveries'}</h1>
              <p className="text-[10px] font-bold text-primary/40 uppercase tracking-widest">{language === 'ar' ? 'تتبع الشحنات والمرتجعات' : 'Shipment Tracking & Returns'}</p>
            </div>
          </div>

        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-t border-border pt-4 mt-2">
          <div className="flex items-center gap-1 overflow-x-auto pb-2 lg:pb-0 scrollbar-none">
            {[
              { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
              { id: 'draft', label: language === 'ar' ? 'مسودة' : 'Draft' },
              { id: 'assigned', label: language === 'ar' ? 'تم التعيين' : 'Assigned' },
              { id: 'shipped', label: language === 'ar' ? 'تم الشحن' : 'Shipped' },
              { id: 'delivered', label: language === 'ar' ? 'تم التوصيل' : 'Delivered' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id as any)}
                className={cn(
                  "px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap shrink-0",
                  filter === tab.id ? "bg-primary text-white shadow-lg" : "text-primary/40 hover:bg-primary/5"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-2 bg-white/50 border border-primary px-4 py-2 rounded-full flex-1 md:w-64">
                <Search className="w-4 h-4 opacity-50" />
                <input
                  type="text"
                  placeholder={language === 'ar' ? 'البحث عن شحنة...' : 'Search shipments...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent border-none outline-none text-sm w-full font-bold"
                />
              </div>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={cn(
                  "p-2.5 rounded-full border transition-all",
                  showAdvancedFilters || dateRange.start || dateRange.end || refFilter || customerFilter || assignedToFilter || minAmount || maxAmount
                    ? "bg-primary text-primary-foreground border-primary shadow-lg"
                    : "bg-white text-primary border-primary/20 hover:border-primary"
                )}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>

            {showAdvancedFilters && (
              <div className="absolute right-0 top-full mt-2 w-full max-w-4xl bg-white border border-primary/20 p-6 rounded-3xl shadow-2xl z-[60] animate-in slide-in-from-top-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                    <input
                      type="text"
                      placeholder="DLV-..."
                      value={refFilter}
                      onChange={e => setRefFilter(e.target.value)}
                      className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5 transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'العميل' : 'Customer'}</label>
                    <input
                      type="text"
                      placeholder={language === 'ar' ? 'اسم العميل...' : 'Customer name...'}
                      value={customerFilter}
                      onChange={e => setCustomerFilter(e.target.value)}
                      className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5 transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'الموظف المسئول' : 'Assigned To'}</label>
                    <input
                      type="text"
                      placeholder={language === 'ar' ? 'اسم الموظف...' : 'Personnel name...'}
                      value={assignedToFilter}
                      onChange={e => setAssignedToFilter(e.target.value)}
                      className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5 transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'التاريخ من' : 'Date From'}</label>
                    <input
                      type="date"
                      className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5 transition-all"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'التاريخ إلى' : 'Date To'}</label>
                    <input
                      type="date"
                      className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5 transition-all"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-primary">{language === 'ar' ? 'النطاق المالي' : 'Value Range'}</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="Min" className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5" />
                      <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="Max" className="w-full bg-white border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-primary/5" />
                    </div>
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <button
                      onClick={() => {
                        setRefFilter('');
                        setCustomerFilter('');
                        setAssignedToFilter('');
                        setDateRange({ start: '', end: '' });
                        setMinAmount('');
                        setMaxAmount('');
                        setSearchQuery('');
                      }}
                      className="px-6 py-2.5 bg-red-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-colors shadow-sm ml-auto"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="relative">
              <button
                onClick={() => setShowColumnGear(!showColumnGear)}
                className="p-2 hover:bg-primary/5 rounded-full transition-all text-primary"
                title="Column selection"
              >
                <Settings className="w-4 h-4 opacity-60 hover:opacity-100" />
              </button>

              {showColumnGear && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl p-4 shadow-xl z-50">
                  <h3 className="text-sm font-bold mb-3">{language === 'ar' ? 'إعدادات الجدول' : 'Table Settings'}</h3>
                  <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                    <p className="text-[10px] uppercase font-bold opacity-40 mb-2">{language === 'ar' ? 'الرؤية والترتيب' : 'Visible & Order'}</p>
                    {columnOrder.map((colId, index) => (
                      <div
                        key={colId}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('columnIndex', index.toString());
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromIndex = parseInt(e.dataTransfer.getData('columnIndex'));
                          const toIndex = index;
                          if (fromIndex === toIndex) return;
                          const newOrder = [...columnOrder];
                          const [movedItem] = newOrder.splice(fromIndex, 1);
                          newOrder.splice(toIndex, 0, movedItem);
                          setColumnOrder(newOrder);
                        }}
                        className="flex items-center gap-2 hover:bg-primary/5 p-2 rounded-xl cursor-move group transition-colors"
                      >
                        <div className="opacity-20 group-hover:opacity-100 transition-opacity">
                          <MoreHorizontal className="w-4 h-4 rotate-90" />
                        </div>
                        <input
                          type="checkbox"
                          checked={columnVisibility[colId]}
                          onChange={() => setColumnVisibility(prev => ({ ...prev, [colId]: !prev[colId] }))}
                          className="rounded border-border accent-primary w-4 h-4 cursor-pointer"
                        />
                        <span className="text-[10px] font-bold text-primary capitalize flex-1 truncate">
                          {String(colId || '').replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div id="deliveries-table-section" className="bg-card border border-border rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table id="deliveries-data-table" className="w-full text-left border-collapse">
            <thead>
              <tr id="deliveries-table-header" className="border-b border-primary/10 bg-primary/5">
                {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                  let label = '';
                  switch(colId) {
                    case 'id': label = language === 'ar' ? 'رقم التوصيل' : 'Delivery No.'; break;
                    case 'invoiceNumber': label = language === 'ar' ? 'فاتورة المصدر' : 'Source Invoice'; break;
                    case 'contact': label = language === 'ar' ? 'العميل' : 'Customer'; break;
                    case 'status': label = language === 'ar' ? 'الحالة' : 'Status'; break;
                    case 'orderedQty': label = language === 'ar' ? 'الكمية المطلوبة' : 'Ordered Qty'; break;
                    case 'deliveredQty': label = language === 'ar' ? 'الكمية المستلمة' : 'Delivered Qty'; break;
                    case 'assignedTo': label = language === 'ar' ? 'المسؤول' : 'Assigned To'; break;
                    case 'createdAt': label = language === 'ar' ? 'تاريخ الإنشاء' : 'Created'; break;
                    case 'deliveredAt': label = language === 'ar' ? 'تاريخ التسليم' : 'Delivered'; break;
                    case 'value': label = language === 'ar' ? 'القيمة' : 'Value'; break;
                  }
                  return (
                    <th key={colId} className={cn(
                      "p-5 text-[10px] font-black uppercase tracking-widest opacity-40 text-primary italic serif",
                      (colId === 'value' || colId === 'orderedQty' || colId === 'deliveredQty') && "text-right"
                    )}>
                      {label}
                    </th>
                  );
                })}
                <th className="p-5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliveries.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-20 text-center text-primary/30 italic serif">
                    No shipments found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredDeliveries.map((dn) => {
                  return (
                    <tr
                      key={dn.id}
                      className="border-b border-primary/5 hover:bg-primary/[0.02] transition-colors group cursor-pointer"
                      onClick={() => {
                        setActiveDelivery(dn);
                        setIsDetailsModalOpen(true);
                      }}
                    >
                      {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                        switch(colId) {
                          case 'id':
                            return <td key={colId} className="p-5 font-mono text-[10px] font-bold opacity-70" dir="ltr">{dn.number}</td>;
                          case 'invoiceNumber':
                            return <td key={colId} className="p-5 font-bold text-[11px] text-primary" dir="ltr">{dn.sourceInvoiceNumber || dn.invoiceNumber || '—'}</td>;
                          case 'contact':
                            return <td key={colId} className="p-5 font-bold text-[11px] text-primary">{dn.contactName}</td>;
                          case 'status':
                            return (
                              <td key={colId} className="p-5">
                                <div className="flex flex-wrap items-center gap-2">
                                  <StatusBadge status={dn.status} language={language} className="text-[9px]" />
                                  <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-[9px] font-bold text-muted-foreground">{logisticsLabel(dn.logisticsStage, language)}</span>
                                </div>
                              </td>
                            );
                          case 'orderedQty':
                            const totalOrdered = (dn.items || []).reduce((sum, item) => sum + (item.quantityOrdered || 0), 0);
                            return <td key={colId} className="p-5 text-right font-bold text-[11px] text-orange-600">{totalOrdered}</td>;
                          case 'deliveredQty':
                            const totalShipped = (dn.items || []).reduce((sum, item) => sum + (item.quantityShipped || 0), 0);
                            return <td key={colId} className="p-5 text-right font-bold text-[11px] text-emerald-600">{totalShipped}</td>;
                          case 'assignedTo':
                            return <td key={colId} className="p-5 text-[11px] font-bold opacity-60 italic serif">{dn.assignedPersonnelName || 'Unassigned'}</td>;
                          case 'createdAt':
                            return <td key={colId} className="p-5 text-[11px] opacity-50">{dn.createdAt?.toDate ? dn.createdAt.toDate().toLocaleDateString() : new Date(dn.createdAt).toLocaleDateString()}</td>;
                          case 'deliveredAt':
                            return <td key={colId} className="p-5 text-[11px] opacity-50">{dn.deliveredAt ? (dn.deliveredAt?.toDate ? dn.deliveredAt.toDate().toLocaleDateString() : new Date(dn.deliveredAt).toLocaleDateString()) : '-'}</td>;
                          case 'value':
                            const val = calculateDeliveryValue(dn);
                            return <td key={colId} className="p-5 text-right font-mono font-bold text-[11px]">{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>;
                          default: return null;
                        }
                      })}
                      <td className="p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                           {dn.status !== 'cancelled' && dn.logisticsStage === 'delivered' && (
                             <button
                               onClick={() => {
                                 setActiveDelivery(dn);
                                 setIsReturnModalOpen(true);
                               }}
                               className="p-2 bg-amber-50 text-amber-600 border border-amber-200 rounded-xl hover:bg-amber-600 hover:text-white transition-all shadow-sm"
                               title="Initiate Return"
                             >
                               <RotateCcw className="w-4 h-4" />
                             </button>
                           )}
                           <button
                             onClick={() => handlePrintDeliveryNote(dn)}
                             className="p-2 bg-white border border-border rounded-xl hover:bg-primary hover:text-white transition-all shadow-sm"
                             title="Print Note"
                           >
                             <Printer className="w-4 h-4" />
                           </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsModalOpen && activeDelivery && (
        <div
          className="fixed inset-0 bg-primary/40 backdrop-blur-sm z-[300] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDetailsModalOpen(false);
          }}
        >
          <div className="bg-white border border-primary w-full max-w-6xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <div className="p-8 border-b border-border bg-primary/5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary rounded-2xl shadow-lg ring-4 ring-primary/10 text-white">
                  <PackageCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold italic serif tracking-tight text-primary">{language === 'ar' ? 'التوصيل' : 'Delivery'} <span dir="ltr">{activeDelivery.number}</span></h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary/40">{language === 'ar' ? 'العميل' : 'Customer'}: {activeDelivery.contactName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePrintDeliveryNote(activeDelivery)}
                  className="p-2 hover:bg-primary/5 rounded-full text-primary transition-all"
                  title="Print PDF"
                >
                  <Printer className="w-6 h-6 transparency-50" />
                </button>
                <button
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="p-2 hover:bg-black/5 rounded-full transition-all"
                >
                  <XCircle className="w-8 h-8 opacity-20" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-8">
              <div className="flex flex-col gap-8 lg:flex-row">
               <div className="min-w-0 flex-1 space-y-8">
               <div className="flex flex-wrap gap-2">
                 {activeDelivery.sourceOrderId && (
                   <button type="button" onClick={() => onNavigate?.('sales_orders', activeDelivery.sourceOrderId)} disabled={!onNavigate} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-xs font-bold text-primary hover:bg-muted disabled:cursor-default disabled:opacity-60">
                     <ExternalLink className="h-3.5 w-3.5" />{language === 'ar' ? 'طلب المصدر' : 'Source Order'}: <span dir="ltr">{activeDelivery.sourceOrderNumber || activeDelivery.sourceOrderId}</span>
                   </button>
                 )}
                 {activeDelivery.sourceInvoiceId && (
                   <button type="button" onClick={() => onNavigate?.('invoices', activeDelivery.sourceInvoiceId)} disabled={!onNavigate} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-xs font-bold text-primary hover:bg-muted disabled:cursor-default disabled:opacity-60">
                     <ExternalLink className="h-3.5 w-3.5" />{language === 'ar' ? 'فاتورة المصدر' : 'Source Invoice'}: <span dir="ltr">{activeDelivery.sourceInvoiceNumber || activeDelivery.invoiceNumber || activeDelivery.sourceInvoiceId}</span>
                   </button>
                 )}
                 {activeDelivery.sourceQuoteId && (
                   <button type="button" onClick={() => onNavigate?.('quotes', activeDelivery.sourceQuoteId)} disabled={!onNavigate} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-xs font-bold text-primary hover:bg-muted disabled:cursor-default disabled:opacity-60">
                     <ExternalLink className="h-3.5 w-3.5" />{language === 'ar' ? 'عرض السعر المصدر' : 'Source Quote'}: <span dir="ltr">{activeDelivery.sourceQuoteNumber || activeDelivery.sourceQuoteId}</span>
                   </button>
                 )}
               </div>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div className="bg-primary/[0.02] p-6 rounded-3xl border border-primary/5">
                   <p className="text-[9px] font-black uppercase tracking-widest opacity-30 mb-2">{language === 'ar' ? 'حالة المستند' : 'Document Status'}</p>
                   <div className="flex flex-wrap gap-2"><StatusBadge status={activeDelivery.status} language={language} /><span className="rounded-full border border-border bg-muted px-3 py-1 text-[10px] font-bold text-muted-foreground">{logisticsLabel(activeDelivery.logisticsStage, language)}</span></div>
                 </div>
                 <div className="bg-primary/[0.02] p-6 rounded-3xl border border-primary/5">
                   <p className="text-[9px] font-black uppercase tracking-widest opacity-30 mb-2">Assigned Personnel</p>
                   <p className="font-bold text-sm text-primary">{activeDelivery.assignedPersonnelName || 'Unassigned'}</p>
                 </div>
                 <div className="bg-primary/[0.02] p-6 rounded-3xl border border-primary/5">
                   <p className="text-[9px] font-black uppercase tracking-widest opacity-30 mb-2">Delivery Date</p>
                   <p className="font-bold text-sm text-primary">
                     {activeDelivery.deliveredAt
                       ? (activeDelivery.deliveredAt?.toDate ? activeDelivery.deliveredAt.toDate().toLocaleString() : new Date(activeDelivery.deliveredAt).toLocaleString())
                       : (activeDelivery.shippedAt ? `Shipped ${activeDelivery.shippedAt?.toDate ? activeDelivery.shippedAt.toDate().toLocaleDateString() : new Date(activeDelivery.shippedAt).toLocaleDateString()}` : 'Pending')}
                   </p>
                 </div>
               </div>

               <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black uppercase tracking-widest text-primary/40">Shipment Manifest</h4>
                    <span className="text-[10px] font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                      {activeDelivery.items.length} Items
                    </span>
                  </div>
                  <div className="border border-primary/10 rounded-3xl overflow-hidden shadow-sm bg-white">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-primary/5">
                          <th className="p-4 text-[9px] font-black uppercase tracking-widest opacity-40">Code</th>
                          <th className="p-4 text-[9px] font-black uppercase tracking-widest opacity-40">{language === 'ar' ? 'الصنف' : 'Item'}</th>
                          <th className="p-4 text-[9px] font-black uppercase tracking-widest opacity-40">{language === 'ar' ? 'معرف سطر عرض السعر' : 'Quote Line ID'}</th>
                          <th className="p-4 text-center text-[9px] font-black uppercase tracking-widest opacity-40">Ordered</th>
                          <th className="p-4 text-center text-[9px] font-black uppercase tracking-widest opacity-40">Shipped</th>
                          <th className="p-4 text-right text-[9px] font-black uppercase tracking-widest opacity-40">Unit Price</th>
                          <th className="p-4 text-right text-[9px] font-black uppercase tracking-widest opacity-40">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeDelivery.items.map((item, idx) => (
                          <tr key={idx} className="border-t border-primary/5">
                            <td className="p-4 text-xs font-mono font-bold opacity-60">{item.itemCode}</td>
                            <td className="p-4 text-xs font-bold text-primary">{item.name}</td>
                            <td className="p-4 text-xs font-mono opacity-60" dir="ltr">{item.quoteLineId || '—'}</td>
                            <td className="p-4 text-center text-xs font-bold opacity-60">{item.quantityOrdered}</td>
                            <td className="p-4 text-center text-xs font-bold text-primary">{item.quantityShipped}</td>
                            <td className="p-4 text-right text-xs font-mono font-bold opacity-60">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td className="p-4 text-right text-xs font-mono font-bold text-primary">{(item.quantityShipped * (item.unitPrice || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end p-6 pr-10">
                    <div className="text-right space-y-1">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-30 italic serif">Shipment Financial Value</p>
                      <p className="text-3xl font-bold text-primary tracking-tighter italic serif underline underline-offset-8 decoration-primary/10">
                        {calculateDeliveryValue(activeDelivery).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
               </div>

               {activeDelivery.logisticsStage === 'delivered' && activeDelivery.signatureUrl && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-primary/10">
                   <div className="space-y-4">
                     <h4 className="text-xs font-black uppercase tracking-widest text-primary/40">Proof of Delivery</h4>
                     <div className="bg-primary/[0.02] border border-primary/10 p-6 rounded-[2rem] flex flex-col items-center">
                        <img src={activeDelivery.signatureUrl} alt="Signature" className="max-h-32 object-contain" />
                        <div className="mt-4 text-center">
                          <p className="text-xs font-bold text-primary">{activeDelivery.receiverName}</p>
                          <p className="text-[9px] font-black uppercase tracking-widest opacity-30">Electronically Signed</p>
                        </div>
                     </div>
                   </div>
                   {activeDelivery.photoUrl && (
                      <div className="space-y-4">
                        <h4 className="text-xs font-black uppercase tracking-widest text-primary/40">Package Photo</h4>
                        <div className="bg-primary/[0.02] border border-primary/10 p-2 rounded-[2rem] overflow-hidden">
                           <img src={activeDelivery.photoUrl} alt="Package" className="w-full aspect-video object-cover rounded-[1.5rem]" />
                        </div>
                      </div>
                   )}
                 </div>
               )}
               </div>
               <DocumentChatterPanel
                 orgId={orgId}
                 entityId={activeDelivery.id}
                 entityType="Delivery"
                 locale={language}
                 defaultCollapsed
                 relatedDocuments={[
                   ...(activeDelivery.sourceOrderId ? [{ id: activeDelivery.sourceOrderId, label: activeDelivery.sourceOrderNumber || (language === 'ar' ? 'طلب المصدر' : 'Source Order'), type: language === 'ar' ? 'طلب المصدر' : 'Source Order' }] : []),
                   ...(activeDelivery.sourceInvoiceId ? [{ id: activeDelivery.sourceInvoiceId, label: activeDelivery.sourceInvoiceNumber || activeDelivery.invoiceNumber || (language === 'ar' ? 'فاتورة المصدر' : 'Source Invoice'), type: language === 'ar' ? 'فاتورة المصدر' : 'Source Invoice' }] : []),
                   ...(activeDelivery.sourceQuoteId ? [{ id: activeDelivery.sourceQuoteId, label: activeDelivery.sourceQuoteNumber || (language === 'ar' ? 'عرض السعر المصدر' : 'Source Quote'), type: language === 'ar' ? 'عرض سعر' : 'Source Quote' }] : []),
                 ]}
                 onRelatedDocumentOpen={document => {
                   if (document.id === activeDelivery.sourceOrderId) onNavigate?.('sales_orders', document.id);
                   else if (document.id === activeDelivery.sourceInvoiceId) onNavigate?.('invoices', document.id);
                   else if (document.id === activeDelivery.sourceQuoteId) onNavigate?.('quotes', document.id);
                 }}
               />
              </div>
            </div>

            <div className="p-8 border-t border-border bg-white flex flex-wrap items-center gap-4">
              {activeDelivery.status !== 'cancelled' && activeDelivery.logisticsStage === 'shipped' && (
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setIsPODModalOpen(true);
                  }}
                  className="flex-1 bg-blue-600 text-white py-5 rounded-[2rem] font-bold uppercase tracking-[0.2em] text-[10px] flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] transition-all shadow-blue-600/20"
                >
                  <Signature className="w-5 h-5" />
                  Confirm Delivery
                </button>
              )}
              {activeDelivery.status !== 'cancelled' && activeDelivery.logisticsStage === 'assigned' && (
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    handleCheckOut(activeDelivery);
                  }}
                  className="flex-1 bg-primary text-white py-5 rounded-[2rem] font-bold uppercase tracking-[0.2em] text-[10px] flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] transition-all shadow-primary-600/20"
                >
                  <Barcode className="w-5 h-5" />
                  Inventory Check-Out
                </button>
              )}
              {activeDelivery.status !== 'cancelled' && activeDelivery.logisticsStage === 'delivered' && (
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setIsReturnModalOpen(true);
                  }}
                  className="flex-1 bg-amber-500 text-white py-5 rounded-[2rem] font-bold uppercase tracking-[0.2em] text-[10px] flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] transition-all shadow-amber-500/20"
                >
                  <RotateCcw className="w-5 h-5" />
                  Initiate Return
                </button>
              )}
              {activeDelivery.status !== 'cancelled' && (
                <button
                  onClick={() => setIsCancelDialogOpen(true)}
                  className="px-6 py-5 text-rose-600 font-black uppercase tracking-widest text-[10px] hover:bg-rose-50 transition-colors border border-rose-200 rounded-[2rem]"
                >
                  {language === 'ar' ? 'إلغاء المستند' : 'Cancel Document'}
                </button>
              )}
              <button
                onClick={() => setIsDetailsModalOpen(false)}
                className="px-8 py-5 text-primary/30 font-black uppercase tracking-widest text-[10px] hover:text-primary transition-colors border border-transparent hover:border-border rounded-[2rem]"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CancelDocumentDialog
        open={isCancelDialogOpen}
        onOpenChange={setIsCancelDialogOpen}
        onConfirm={handleCancelDelivery}
        pending={isCancelling}
        language={language}
        documentLabel={activeDelivery?.number}
      />

      {/* Return Modal */}
      {isReturnModalOpen && activeDelivery && (
        <div
          className="fixed inset-0 bg-primary/40 backdrop-blur-sm z-[300] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsReturnModalOpen(false);
          }}
        >
          <div className="bg-white border border-primary w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-border bg-amber-50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500 rounded-2xl shadow-lg ring-4 ring-amber-500/10 text-white">
                  <RotateCcw className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold italic serif tracking-tight text-primary">Item Return</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary/40">{language === 'ar' ? 'من التوصيل' : 'From Delivery'} <span dir="ltr">{activeDelivery.number}</span></p>
                </div>
              </div>
              <button
                onClick={() => setIsReturnModalOpen(false)}
                className="p-2 hover:bg-black/5 rounded-full transition-all"
              >
                <XCircle className="w-8 h-8 opacity-20" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
               <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10">
                  <p className="text-[10px] font-black uppercase tracking-widest text-primary/30 mb-4">Original Delivery Manifest</p>
                  <div className="space-y-3">
                    {activeDelivery.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/50 p-4 rounded-2xl border border-border shadow-sm">
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-primary">{item.name}</p>
                          <p className="text-[9px] font-black opacity-30 uppercase tracking-widest">Shipped: {item.quantityShipped}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-primary/40">ReturnQty</span>
                            <div className="flex items-center gap-2 bg-white border border-border rounded-xl px-2 py-1">
                              <button
                                onClick={() => setReturnQuantities(prev => ({
                                  ...prev,
                                  [item.itemId]: Math.max(0, (prev[item.itemId] || 0) - 1)
                                }))}
                                className="w-6 h-6 flex items-center justify-center hover:bg-amber-50 rounded-lg text-amber-600 transition-all"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                value={returnQuantities[item.itemId] || 0}
                                onChange={(e) => setReturnQuantities(prev => ({
                                  ...prev,
                                  [item.itemId]: Math.min(item.quantityShipped, parseInt(e.target.value) || 0)
                                }))}
                                className="w-12 text-center text-xs font-bold bg-transparent outline-none border-none pointer-events-none"
                              />
                              <button
                                onClick={() => setReturnQuantities(prev => ({
                                  ...prev,
                                  [item.itemId]: Math.min(item.quantityShipped, (prev[item.itemId] || 0) + 1)
                                }))}
                                className="w-6 h-6 flex items-center justify-center hover:bg-amber-50 rounded-lg text-amber-600 transition-all"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
            </div>

            <div className="p-8 border-t border-border bg-white flex flex-col gap-3">
              <button
                onClick={handleConfirmReturn}
                disabled={isSubmittingReturn || !Object.values(returnQuantities).some(q => q > 0)}
                className="w-full bg-amber-500 text-white py-5 rounded-[2rem] font-bold uppercase tracking-[0.2em] text-[10px] flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 shadow-amber-500/20"
              >
                {isSubmittingReturn ? <Loader2 className="w-5 h-5 animate-spin" /> : <RotateCcw className="w-5 h-5" />}
                Confirm Return to Stock
              </button>
              <button
                onClick={() => setIsReturnModalOpen(false)}
                className="w-full py-4 text-primary/30 font-black uppercase tracking-widest text-[10px] hover:text-primary transition-colors"
                disabled={isSubmittingReturn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Check Out Modal */}
      {isCheckOutModalOpen && activeDelivery && (
        <div
          className="fixed inset-0 bg-[#141414]/90 backdrop-blur-md z-[300] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsCheckOutModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-xl rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 bg-primary text-primary-foreground border-b border-[#141414] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <Barcode className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold italic serif tracking-tight">Inventory Check-Out</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">{language === 'ar' ? 'فحص مكونات' : 'Scanning components for'} <span dir="ltr">{activeDelivery.number}</span></p>
                </div>
              </div>
              <button
                onClick={() => setIsCheckOutModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-all"
              >
                <XCircle className="w-8 h-8" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">
              {loadingStock ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <Loader2 className="w-12 h-12 animate-spin text-primary/20" />
                  <p className="text-xs font-bold uppercase tracking-widest opacity-40">Verifying stock levels...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(activeDelivery.items || []).map((item, idx) => {
                    const status = stockStatus[item.itemId];
                    return (
                      <div key={idx} className={cn(
                        "p-6 rounded-3xl border flex items-center justify-between transition-all",
                        status?.trackInventory
                          ? status.available
                            ? "bg-green-50/50 border-green-200 shadow-sm"
                            : "bg-red-50/50 border-red-200 shadow-lg ring-1 ring-red-500/10"
                          : "bg-white/40 border-[#141414]/5 opacity-60"
                      )}>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black opacity-30 uppercase tracking-widest leading-none mb-1">
                            SKU: {item.itemCode}
                          </p>
                          <p className="text-lg font-bold text-[#141414]">{item.name || item.description}</p>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-primary">Required: {item.quantityShipped}</span>
                            {status && (
                              <span className={cn(
                                "text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border",
                                status.trackInventory
                                  ? (status.available ? "bg-green-100 text-green-700 border-green-300" : "bg-red-100 text-red-700 border-red-300")
                                  : "bg-gray-100 text-gray-500 border-gray-300"
                              )}>
                                On Hand: {status.onHand}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {status?.trackInventory ? (
                            status.available ? (
                              <div className="flex items-center gap-2 text-green-600 font-bold text-[9px] uppercase tracking-widest">
                                <CheckCircle2 className="w-5 h-5" />
                                <span>Available</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-red-600 font-bold text-[9px] uppercase tracking-widest">
                                <XCircle className="w-5 h-5" />
                                <span>Shortage</span>
                              </div>
                            )
                          ) : (
                            <div className="flex items-center gap-2 text-green-600/50 font-bold text-[9px] uppercase tracking-widest">
                               <CheckCircle2 className="w-5 h-5 opacity-50" />
                               <span>Ready</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-8 border-t border-[#141414]/10 bg-white flex flex-col gap-4 shadow-2xl">
              <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10 flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] opacity-40">Ready for Shipment</p>
                  <p className="text-lg font-bold text-primary italic serif">
                    {(activeDelivery.items || []).filter(i => !stockStatus[i.itemId]?.trackInventory || stockStatus[i.itemId]?.available).length} of {(activeDelivery.items || []).length} Items Validated
                  </p>
                </div>
                {(activeDelivery.items || []).every(i => !stockStatus[i.itemId]?.trackInventory || stockStatus[i.itemId]?.available) ? (
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg">
                    <ClipboardCheck className="w-6 h-6" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setIsCheckOutModalOpen(false)}
                  className="py-5 px-6 bg-white border border-[#141414]/10 rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-[#141414] hover:text-white transition-all shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={finalizeCheckOut}
                  disabled={loadingStock || isFinalizingCheckOut || !(activeDelivery.items || []).every(i => !stockStatus[i.itemId]?.trackInventory || stockStatus[i.itemId]?.available)}
                  className="py-5 px-6 bg-primary text-primary-foreground rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-primary/90 flex items-center justify-center gap-2 transition-all shadow-xl disabled:opacity-50 disabled:grayscale"
                >
                  {isFinalizingCheckOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                  Finalize Check-Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POD Modal (Similar to the one in InvoicesView for consistency) */}
      {isPODModalOpen && activeDelivery && (
        <div
          className="fixed inset-0 bg-[#141414]/90 backdrop-blur-md z-[300] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPODModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-blue-600 text-white shadow-md">
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6" />
                <h3 className="text-xl font-bold italic serif tracking-tight">Confirm Delivery</h3>
              </div>
              <button onClick={() => setIsPODModalOpen(false)} className="p-1 hover:bg-white/10 rounded-full transition-all">
                <XCircle className="w-8 h-8" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest mb-1.5 block text-center">Receiver Name (Required)</label>
                  <input
                    type="text"
                    required
                    value={podData.receiverName}
                    onChange={(e) => setPodData({ ...podData, receiverName: e.target.value })}
                    placeholder="Who received the package?"
                    className="w-full bg-white border border-[#141414] p-4 rounded-xl font-bold text-sm focus:ring-4 focus:ring-blue-500/10 outline-none shadow-sm text-center"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest mb-1.5 block text-center">Digital Signature (Required)</label>
                  <div className="border-2 border-dashed border-[#141414]/20 rounded-2xl bg-white overflow-hidden shadow-inner relative group">
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      canvasProps={{
                        className: 'w-full h-48 cursor-crosshair'
                      }}
                    />
                    <button
                      onClick={() => sigCanvas.current?.clear()}
                      className="absolute top-2 right-2 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-[#141414]/50 uppercase tracking-widest mb-1.5 block text-center">Package Photo</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      id="pod-photo-deliveries"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setPodData({ ...podData, photo: file });
                      }}
                    />
                    <button
                      onClick={() => document.getElementById('pod-photo-deliveries')?.click()}
                      className={cn(
                        "w-full flex items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed transition-all",
                        podData.photo ? "bg-green-50 border-green-500 text-green-700 font-bold" : "bg-white border-[#141414]/10 text-[#141414]/40 hover:border-blue-500 hover:text-blue-500"
                      )}
                    >
                      {podData.photo ? (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Photo Linked</span>
                        </>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Camera className="w-8 h-8" />
                          <span className="text-[10px] uppercase font-bold tracking-widest">Take Package Photo</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-[#141414]/10 bg-white flex flex-col gap-3 shadow-2xl">
              <button
                onClick={handleConfirmPOD}
                disabled={isSubmittingPOD}
                className="w-full bg-blue-600 text-white py-5 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-blue-700 transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isSubmittingPOD ? <Loader2 className="w-5 h-5 animate-spin" /> : <Signature className="w-5 h-5" />}
                <span>Complete Delivery</span>
              </button>
              <button
                onClick={() => setIsPODModalOpen(false)}
                disabled={isSubmittingPOD}
                className="w-full py-4 text-[#141414]/40 font-bold uppercase tracking-widest text-[10px] hover:text-[#141414] transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Print Modal */}
      {isPrintModalOpen && docToPrint && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[250] flex flex-col items-center overflow-auto p-8 print:p-0 print:bg-white print:block">
          <div className="flex flex-col gap-6 w-full max-w-[850px] print:w-full print:max-w-none">
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-lg p-4 rounded-2xl border border-primary/10 shadow-lg print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight">Print Preview</h3>
                  <p className="text-[10px] opacity-50 font-bold uppercase tracking-widest">DN-{docToPrint.id?.slice(-8).toUpperCase()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-xs hover:opacity-90 transition-all shadow-md active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  Print Now
                </button>
                <button
                  onClick={() => setIsPrintModalOpen(false)}
                  className="p-2.5 hover:bg-red-50 text-red-600 rounded-xl transition-all"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="bg-white shadow-2xl rounded-sm overflow-hidden print:shadow-none print:rounded-none">
              <DocumentPrintLayout
                organization={organization}
                document={docToPrint}
                contact={null} // Deliveries have contactName on them
                type="delivery_note"
                language={language}
              />
            </div>

            <div className="flex justify-center pb-12 print:hidden">
               <p className="text-[10px] items-center gap-2 flex font-bold uppercase tracking-widest opacity-40">
                  <span className="w-2 h-2 bg-primary/20 rounded-full animate-pulse" />
                  Standard Odoo-Matching Print Layout
               </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
